import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";
    const nav = useNavigate();
    const { refresh } = useAuth();
    const [state, setState] = useState({ status: "checking" });
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!token) {
            setState({ status: "invalid" });
            return;
        }
        api.get(`/auth/invite/${token}`)
            .then((r) => setState({ status: "ready", ...r.data }))
            .catch((err) => setState({ status: "invalid", error: formatApiErrorDetail(err.response?.data?.detail) }));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
        if (password !== confirm) { toast.error("Passwords don't match"); return; }
        setSubmitting(true);
        try {
            await api.post("/auth/accept-invite", { token, password });
            await refresh();
            toast.success("Welcome to Players Health BD");
            nav("/app");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to accept invite");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-ph-canvas p-6">
            <div className="w-full max-w-md">
                <div className="flex items-center justify-center mb-8">
                    <img src="/players-health-logo.png" alt="Players Health" className="h-9 w-auto" draggable={false} />
                </div>
                <div className="ph-card p-7">
                    {state.status === "checking" && (
                        <div className="text-sm text-ph-muted text-center py-4">Validating invitation…</div>
                    )}
                    {state.status === "invalid" && (
                        <div className="text-center py-2">
                            <div className="w-12 h-12 rounded-full bg-rose-50 mx-auto flex items-center justify-center mb-4">
                                <AlertCircle className="w-6 h-6 text-rose-600" />
                            </div>
                            <h1 className="text-xl font-semibold text-ph-text">This invitation isn't valid</h1>
                            <p className="text-sm text-ph-muted mt-2">
                                {state.error || "It may have expired or already been used. Ask an admin to send a new invite."}
                            </p>
                            <Link to="/login" className="inline-block mt-6 text-sm text-ph-primary font-medium hover:text-ph-primaryHover">
                                Back to sign in →
                            </Link>
                        </div>
                    )}
                    {state.status === "ready" && (
                        <>
                            <h1 className="text-xl font-semibold text-ph-text">Welcome to Players Health BD</h1>
                            <p className="text-sm text-ph-muted mt-1">
                                Set a password for <span className="text-ph-text font-medium">{state.email}</span> to finish creating your account.
                            </p>
                            <div className="mt-4 px-3 py-2 rounded-md bg-ph-canvas border border-ph-border text-xs text-ph-muted">
                                Account: <span className="text-ph-text font-medium">{state.name}</span> · Role:{" "}
                                <span className="text-ph-text font-medium">{state.role === "admin" ? "Admin" : "Member"}</span>
                            </div>
                            <form onSubmit={submit} className="space-y-4 mt-5" data-testid="accept-invite-form">
                                <div>
                                    <label className="text-xs font-medium text-ph-text mb-1.5 block">Choose a password</label>
                                    <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="h-10" data-testid="invite-password-input" />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-ph-text mb-1.5 block">Confirm password</label>
                                    <Input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-10" data-testid="invite-password-confirm" />
                                </div>
                                <Button type="submit" disabled={submitting} className="w-full h-10 bg-ph-primary text-white font-medium hover:bg-ph-primaryHover" data-testid="accept-invite-btn">
                                    {submitting ? "Creating account…" : "Accept invite & sign in"}
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
